import React, { useState, useEffect } from "react";
import {
  RegisterIntroduction,
  RegisterSelection,
  RegisterSteps,
  RegisterForm,
  UserProgramCards,
} from "../../../components";
import { BlockingContainer, RegistrationContainer } from "./registration.styled";
import { SubjectSVG } from "src/assets";
import {
  useGetCareersQuery,
  useGetSelectedSubjectsMutation,
  useGetSelectionDataByCareerPeriodMutation,
  useGetSubjectsByCareerPeriodMutation,
  useGetSufficiencySubjectsMutation,
  useRegisterSufficiencySubjectMutation,
} from "src/services";
import { useSelector } from "react-redux";
import { RootState } from "@/core/store/store";
import { Button, Card, Image, List, Modal, Skeleton } from "antd";
import {
  LargeHeadingStyled,
  SmallHeadingStyled,
  XsHeadingStyled,
} from "src/components/custom/texts/texts.styled";
import { IntroductionContainer } from "src/components/registration/register-introduction/register-introduction.styled";
import { Box, Drawer, Paragraph } from "@nubeteck/components";
import { RegisterSVG } from "src/assets";
import { ISelectedSubject, ISubject } from "@/Interfaces/subject";
import useManageTimer from "src/hooks/useManageTimer";
import { FormatterUtils } from "@nubeteck/utils";
import { getEnrollmentPage } from "../../../Api/content";
import { getUserEnrollementPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../../pages/StrapiError";

interface IRegisterStepProps {
  id: number;
  title: string;
  component: JSX.Element;
}

interface IBloqueo {
  bloqueo: string;
  bloqueoDescripcionLarga: string;
  departamentoCorrespondiente: string;
  matricula: string;
  razon: string;
}

const Registration = () => {
  const { userInfo } = useSelector((state: RootState) => state.user);
  const { locale } = useLocale();
  const [enrollmentPage, setEnrollmentPage] = useState<any>(null);
  const [userEnrollementPage, setUserEnrollementPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [careerForSelectionId, setCareerForSelectionId] = useState<number>(0);
  const [PeriodForSelectionId, setPeriodForSelectionId] = useState<number>(0);
  const [subjectsDrawerOpen, setSubjectsDrawerOpen] = useState(false);
  const [sufficiencyDrawerVisible, setSufficiencyDrawerVisible] = useState(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [getSufficiencySubjects, { data: sufficiencySubjects, isLoading: isLoadingSufficiencies }] =
    useGetSufficiencySubjectsMutation();
  const [
    registerSufficiency,
    {
      isLoading: isLoadingRegisterSufficiency,
      isSuccess: isSuccessRegisterSufficiency,
      reset: resetRegisterSufficiency,
    },
  ] = useRegisterSufficiencySubjectMutation();

  const { getTimer, selectionMinutes } = useManageTimer(
    currentStep === 0,
    true,
    isSuccessRegisterSufficiency,
  );
  const matriculaAdministrativa = localStorage.getItem("matriculaAdministrativa") === "true";
  const { data: careers, isLoading: isLoadingCareers } = useGetCareersQuery(
    matriculaAdministrativa,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [getSelectedSections, { data: selectedData, isLoading: isLoadingSelected }] =
    useGetSelectedSubjectsMutation();
  const [getSubjects, { data: subjects, isLoading: isLoadingSubjects }] =
    useGetSubjectsByCareerPeriodMutation();
  const [getSelectionData, { data: selectionData, isLoading: isLoadingSelectionData }] =
    useGetSelectionDataByCareerPeriodMutation();

  const obtenerProyeccionAsignaturas = () => {
    getSubjects({
      careerId: careerForSelectionId,
      periodId: PeriodForSelectionId,
      esPrematricula: matriculaAdministrativa,
    });
  };

  React.useEffect(() => {
    if (careerForSelectionId !== 0) {
      obtenerProyeccionAsignaturas();
      getSelectedSections(careerForSelectionId);
      getSelectionData({
        careerId: careerForSelectionId,
        periodId: PeriodForSelectionId,
        esPrematricula: matriculaAdministrativa,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careerForSelectionId]);

  React.useEffect(() => {
    if (isSuccessRegisterSufficiency) {
      getSufficiencySubjects({ careerId: careerForSelectionId, periodId: PeriodForSelectionId });
      obtenerProyeccionAsignaturas();
      resetRegisterSufficiency();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessRegisterSufficiency]);

  const stepIncrease = () => {
    if (currentStep < 2) setCurrentStep(currentStep + 1);
  };
  const stepDecrease = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setEnrollmentPage(null);
        setUserEnrollementPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    const loadContent = async () => {
      try {
        const [enrollmentRes, userEnrollementRes] = await Promise.all([
          getEnrollmentPage(locale),
          getUserEnrollementPage(locale)
        ]);
        
        if (!cancel) {
          clearTimeout(timeoutId);
          setEnrollmentPage(enrollmentRes?.data?.attributes || enrollmentRes?.data || enrollmentRes);
          setUserEnrollementPage(userEnrollementRes?.data?.attributes || userEnrollementRes?.data || userEnrollementRes);
          setIsLoadingContent(false);
        }
      } catch (error) {
        if (!cancel) {
          clearTimeout(timeoutId);
          setEnrollmentPage(null);
          setUserEnrollementPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      }
    };
    
    loadContent();
    
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  const steps: IRegisterStepProps[] = [
    {
      id: 0,
      title: enrollmentPage?.subtitleO || "Inicio",
      component: (
        <RegisterIntroduction
          careers={careers?.data}
          setCareer={setCareerForSelectionId}
          setPeriod={setPeriodForSelectionId}
          isLoading={isLoadingCareers}
          goNext={stepIncrease}
          timeValue={selectionMinutes}
        />
      ),
    },
    {
      id: 1,
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (enrollmentPage?.subtitleT || "Selección de materias"),
      component: (
        <RegisterSelection
          periodId={PeriodForSelectionId}
          subjectsData={subjects?.data?.seleccionAsignaturas}
          registeredSubjects={subjects?.data?.inscritasOPorValidarAsignaturas ?? []}
          isLoading={
            isLoadingSubjects ||
            isLoadingSelectionData ||
            isLoadingSelected ||
            isLoadingRegisterSufficiency
          }
          selectedData={selectedData?.data}
          setSufficiencyDrawerVisible={() => {
            getSufficiencySubjects({
              careerId: careerForSelectionId,
              periodId: PeriodForSelectionId,
            });
            setSufficiencyDrawerVisible(true);
          }}
          updateSubjects={() => {
            obtenerProyeccionAsignaturas();
            getSelectionData({
              careerId: careerForSelectionId,
              periodId: PeriodForSelectionId,
              esPrematricula: matriculaAdministrativa,
            });
            getSelectedSections(careerForSelectionId);
          }}
        />
      ),
    },
    {
      id: 2,
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (enrollmentPage?.subtitleTR || "Carrito"),
      component: <></>,
    },
    {
      id: 3,
      title: isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (enrollmentPage?.subtitleF || "Caja"),
      component: <></>,
    },
  ];

  const decideStepRendering = () => {
    return steps?.find((step) => step.id === currentStep)?.component;
  };

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setEnrollmentPage(null);
        setUserEnrollementPage(null);
      }}
    />;
  }
  return (
    <RegistrationContainer>
      <RegisterSteps
        steps={steps.map((step) => ({ title: step.title }))}
        currentStep={currentStep}
        getExternalTimer={getTimer}
      />
      {currentStep > 0 && (
        <UserProgramCards
          isLoading={isLoadingSelectionData}
          userInfo={{
            ...selectionData?.data,
            monedaSimbolo: subjects?.data?.seleccionAsignaturas[0]?.monedaId === 2 ? "₡" : "$",
          }}
          setSelectedSubjectsVisible={() => setSubjectsDrawerOpen(true)}
        />
      )}
      {!userInfo.hayProcesoAbierto ? (
        <Card>
          <IntroductionContainer>
            <Box>
              <LargeHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 300 }} /> : (enrollmentPage?.subtitleSv || "No hay ningún proceso de matrícula abierto")}</LargeHeadingStyled>
              <p>
                {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 400 }} /> : (enrollmentPage?.description || "Espera a que haya algún periodo disponible y vuelve para seleccionar tus asignaturas.")}
              </p>
            </Box>
            <Image src={RegisterSVG} width={285} alt={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 285 }} /> : (enrollmentPage?.register_ilustration || "Ilustración registro")} preview={false} />
          </IntroductionContainer>
        </Card>
      ) : userInfo.bloqueos?.length > 0 ? (
        <Card>
          <IntroductionContainer>
            <Box>
              <LargeHeadingStyled>
                {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 300 }} /> : (enrollmentPage?.descriptionT || "Su acceso al proceso de matrícula ha sido bloqueado.")}
              </LargeHeadingStyled>
              {userInfo.bloqueos?.map((bloq: IBloqueo) => (
                <Box key={`${bloq.bloqueo} ${bloq.razon} ${bloq.departamentoCorrespondiente}`}>
                  <BlockingContainer
                    key={`${bloq.bloqueo} ${bloq.razon} ${bloq.departamentoCorrespondiente}`}
                  >
                    <SmallHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (enrollmentPage?.bloqued || "Bloqueo")}: {bloq.razon}</SmallHeadingStyled>
                    <p>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (enrollmentPage?.descriptionTR || "Descripción")}: {bloq.bloqueoDescripcionLarga}.</p>
                    <p>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (enrollmentPage?.messageTR || "Comuníquese con el departamento de")} {bloq.departamentoCorrespondiente}.</p>
                  </BlockingContainer>
                </Box>
              ))}
            </Box>
            <Image src={RegisterSVG} width={285} alt={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 285 }} /> : (enrollmentPage?.register_ilustration || "Ilustración registro")} preview={false} />
          </IntroductionContainer>
        </Card>
      ) : (
        <RegisterForm
          careers={careers?.data}
          callbackIfPreviousRoute={(program: number, period: number) => {
            setCareerForSelectionId(program);
            setPeriodForSelectionId(period);
            setCurrentStep(1);
          }}
          formCurrentItem={decideStepRendering()}
          readyForSubmit={currentStep === 1}
          goBack={stepDecrease}
          goToNext={stepIncrease}
          stepIndex={currentStep}
        />
      )}

      <Drawer
        placement="right"
        title={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 200 }} /> : (userEnrollementPage?.couse_selection?.subject_subtitle || "Asignaturas seleccionadas")}
        open={subjectsDrawerOpen}
        onClose={() => setSubjectsDrawerOpen(false)}
      >
        <List
          loading={isLoadingSelected}
          dataSource={selectedData?.data}
          renderItem={(item: ISelectedSubject & { seccionPrecio: number }) => (
            <List.Item key={item.asignaturaId}>
              <List.Item.Meta
                avatar={<Image preview={false} src={SubjectSVG} width={35} />}
                title={
                  <SmallHeadingStyled style={{ margin: "0px 0px 10px 0px" }}>
                    {item.asignaturaNombre}
                  </SmallHeadingStyled>
                }
                description={
                  <>
                    <XsHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection?.subject_credit || "Créditos")}: {item.creditos}</XsHeadingStyled>
                    <XsHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection?.subject_cost || "Costo")}: {item.monedaId === 1 ? "$" : "₡"}{" "}
                      {FormatterUtils.number(`${item.seccionPrecio}`)}
                    </XsHeadingStyled>
                    {item.horarios?.map((hor) => (
                      <div key={`${hor.diaId}${hor.horaFin}${hor.horaInicio}`}>
                        <Paragraph
                          type="secondary"
                          style={{ marginBottom: "3px", color: "#000000d6" }}
                        >
                          {hor.diaNombre}, {hor.horaInicio} - {hor.horaFin}
                        </Paragraph>
                      </div>
                    ))}
                  </>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>

      <Modal
        title={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (enrollmentPage?.subtitleS || "Agregar asignatura por suficiencia")}
        open={sufficiencyDrawerVisible}
        onCancel={() => setSufficiencyDrawerVisible(false)}
        footer={
          <Button type="primary" onClick={() => setSufficiencyDrawerVisible(false)}>
            {isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (enrollmentPage?.close_button || "Cerrar")}
          </Button>
        }
      >
        <List
          loading={isLoadingSufficiencies || isLoadingRegisterSufficiency}
          dataSource={sufficiencySubjects?.data}
          renderItem={(item: ISubject) => (
            <List.Item key={item.asignaturaId}>
              <List.Item.Meta
                avatar={<Image preview={false} src={SubjectSVG} width={35} />}
                title={
                  <SmallHeadingStyled style={{ margin: "0px 0px 10px 0px" }}>
                    {item.asignaturaNombre}
                  </SmallHeadingStyled>
                }
                description={
                  <>
                    <XsHeadingStyled>{isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 100 }} /> : (userEnrollementPage?.couse_selection?.subject_credit || "Créditos")}: {item.creditos}</XsHeadingStyled>
                    <Button
                      onClick={() =>
                        registerSufficiency({
                          asignaturaId: item.asignaturaId,
                          periodoId: PeriodForSelectionId,
                          carreraId: careerForSelectionId,
                        })
                      }
                    >
                      {enrollmentPage?.add_button || "Agregar"}
                    </Button>
                  </>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </RegistrationContainer>
  );
};

export default Registration;
